import { useRef, useEffect, useCallback, useState } from 'react';
import { gsap } from 'gsap';
import './MagicBento.css';

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 300;
const DEFAULT_GLOW_COLOR = '82, 40, 245';
const MOBILE_BREAKPOINT = 768;

/* ─── SVG icons (no emojis) ─────────────────────────────── */
const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke="#a78bfa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconSend = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconZap = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconShield = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
    <path d="M12 2l7 3v5c0 5-3.5 9.3-7 11-3.5-1.7-7-6-7-11V5l7-3z" stroke="#5228F5" strokeWidth="1.5" fill="rgba(82,40,245,0.12)"/>
    <path d="M9 12l2 2 4-4" stroke="#a78bfa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPhone = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="18" r="1" fill="currentColor"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconAU = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="15" fill="rgba(82,40,245,0.15)" stroke="rgba(82,40,245,0.3)" strokeWidth="1"/>
    <text x="16" y="21" textAnchor="middle" fontSize="16">🇦🇺</text>
  </svg>
);

/* ─── Card visuals ─────────────────────────────────────── */

/* Card 1 — mini SMS thread */
const CardVisual1 = () => (
  <div className="mb-visual mb-sms-thread">
    <div className="mb-bubble mb-bubble--out">
      <span>Your appointment is confirmed for 3pm</span>
      <span className="mb-bubble-time">09:41 <IconCheck /></span>
    </div>
    <div className="mb-bubble mb-bubble--in">
      <span>Thanks, see you then!</span>
      <span className="mb-bubble-time">09:41</span>
    </div>
    <div className="mb-bubble mb-bubble--out mb-bubble--latest">
      <span>Reminder: your appt is in 1 hour</span>
      <span className="mb-bubble-time">14:00 <IconCheck /></span>
    </div>
    <div className="mb-delivery-badge"><IconCheck /> Delivered in &lt;1s</div>
  </div>
);

/* Card 2 — stats */
const CardVisual2 = () => (
  <div className="mb-visual mb-stats">
    <div className="mb-stat-row">
      <div className="mb-stat-big">
        <span className="mb-stat-num">99.9<span className="mb-stat-unit">%</span></span>
        <span className="mb-stat-label">Delivery rate</span>
      </div>
      <div className="mb-stat-divider" />
      <div className="mb-stat-big">
        <span className="mb-stat-num">&lt;1<span className="mb-stat-unit">s</span></span>
        <span className="mb-stat-label">Avg latency</span>
      </div>
    </div>
    <div className="mb-sparkline">
      <svg viewBox="0 0 120 36" fill="none" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5228F5" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#5228F5" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d="M0 28 C10 26,20 20,30 22 C40 24,50 10,60 8 C70 6,80 14,90 12 C100 10,110 6,120 4" stroke="#7c3aed" strokeWidth="2" fill="none"/>
        <path d="M0 28 C10 26,20 20,30 22 C40 24,50 10,60 8 C70 6,80 14,90 12 C100 10,110 6,120 4 L120 36 L0 36Z" fill="url(#sg)"/>
      </svg>
    </div>
  </div>
);

/* Card 3 — integration logos */
const CardVisual3 = () => (
  <div className="mb-visual mb-integrations">
    {[
      { src: 'https://cdn.simpleicons.org/zapier/ffffff', name: 'Zapier', mono: true },
      { src: '/logos/icons/power-automate.png', name: 'Power Automate', mono: false },
      { src: 'https://cdn.simpleicons.org/supabase/ffffff', name: 'Supabase', mono: true },
      { src: 'https://cdn.simpleicons.org/auth0/ffffff', name: 'Auth0', mono: true },
      { src: '/logos/icons/teams.png', name: 'Teams', mono: false },
      { src: 'https://cdn.simpleicons.org/wordpress/ffffff', name: 'WordPress', mono: true },
    ].map((logo, i) => (
      <div key={i} className="mb-logo-chip" style={{ '--d': `${i * 0.06}s` }}>
        <img src={logo.src} alt={logo.name} width="16" height="16" style={logo.mono ? { filter: 'brightness(0) invert(1)', opacity: 0.8 } : { opacity: 0.95 }} />
        <span>{logo.name}</span>
      </div>
    ))}
    <div className="mb-logo-chip mb-logo-chip--more">+5,994 more</div>
  </div>
);

/* Card 4 — automation flow (SVG icons, no emojis) */
const CardVisual4 = () => (
  <div className="mb-visual mb-flow">
    {[
      { Icon: IconCalendar, label: 'Appointment booked', color: '#5228F5' },
      { Icon: IconZap,      label: 'Trigger fires',      color: '#7c3aed' },
      { Icon: IconPhone,    label: 'SMS sent instantly',  color: '#a78bfa' },
    ].map(({ Icon, label, color }, i) => (
      <div key={i} className="mb-flow-row">
        <div className="mb-flow-step" style={{ '--accent': color }}>
          <span className="mb-flow-icon-wrap" style={{ color }}><Icon /></span>
          <span className="mb-flow-label">{label}</span>
        </div>
        {i < 2 && <div className="mb-flow-arrow">↓</div>}
      </div>
    ))}
  </div>
);

/* Card 5 — AU number */
const CardVisual5 = () => (
  <div className="mb-visual mb-au">
    <div className="mb-au-ring">
      <svg viewBox="0 0 80 80" width="80" height="80" fill="none">
        <circle cx="40" cy="40" r="36" stroke="rgba(82,40,245,0.2)" strokeWidth="1.5"/>
        <circle cx="40" cy="40" r="28" stroke="rgba(82,40,245,0.12)" strokeWidth="1"/>
        <text x="40" y="48" textAnchor="middle" fontSize="26" fontFamily="system-ui">🇦🇺</text>
      </svg>
    </div>
    <div className="mb-au-number">+61 4XX XXX XXX</div>
    <div className="mb-au-sub">Dedicated sender ID</div>
  </div>
);

/* Card 6 — security */
const CardVisual6 = () => (
  <div className="mb-visual mb-security">
    <IconShield />
    <div className="mb-sec-tags">
      {['TLS 1.3', 'AES-256', 'ACMA'].map(t => (
        <span key={t} className="mb-sec-tag">{t}</span>
      ))}
    </div>
  </div>
);

const cardData = [
  { label: 'Speed',        title: 'Instant Delivery',    description: 'Messages reach customers in seconds, not minutes',       Visual: CardVisual1 },
  { label: 'Analytics',    title: 'Rich Analytics',      description: 'Track delivery, opens and engagement in real-time',      Visual: CardVisual2 },
  { label: 'Integrations', title: '6,000+ Integrations', description: 'Connect with Zapier, Power Automate, Supabase and more', Visual: CardVisual3 },
  { label: 'Automation',   title: 'Smart Automation',    description: 'Trigger SMS from any event — no code required',          Visual: CardVisual4 },
  { label: 'Local',        title: 'Australian Numbers',  description: 'Local presence with dedicated Aussie sender IDs',        Visual: CardVisual5 },
  { label: 'Security',     title: 'Enterprise Security', description: 'End-to-end encryption and full compliance built in',     Visual: CardVisual6 },
];

/* ─── Particle helpers ──────────────────────────────────── */
const createParticleElement = (x, y, color = DEFAULT_GLOW_COLOR) => {
  const el = document.createElement('div');
  el.style.cssText = `position:absolute;width:4px;height:4px;border-radius:50%;background:rgba(${color},1);box-shadow:0 0 6px rgba(${color},0.6);pointer-events:none;z-index:100;left:${x}px;top:${y}px;`;
  return el;
};
const calcSpot = r => ({ proximity: r * 0.5, fadeDistance: r * 0.75 });
const setGlow = (card, mx, my, g, r) => {
  const rect = card.getBoundingClientRect();
  card.style.setProperty('--glow-x', `${((mx - rect.left) / rect.width) * 100}%`);
  card.style.setProperty('--glow-y', `${((my - rect.top) / rect.height) * 100}%`);
  card.style.setProperty('--glow-intensity', g.toString());
  card.style.setProperty('--glow-radius', `${r}px`);
};

/* ─── ParticleCard ──────────────────────────────────────── */
const ParticleCard = ({ children, className, style, particleCount = DEFAULT_PARTICLE_COUNT, glowColor = DEFAULT_GLOW_COLOR, clickEffect, disableAnimations }) => {
  const ref = useRef(null);
  const particles = useRef([]);
  const timeouts = useRef([]);
  const hovered = useRef(false);
  const memo = useRef([]);
  const inited = useRef(false);

  const init = useCallback(() => {
    if (inited.current || !ref.current) return;
    const { width, height } = ref.current.getBoundingClientRect();
    memo.current = Array.from({ length: particleCount }, () => createParticleElement(Math.random() * width, Math.random() * height, glowColor));
    inited.current = true;
  }, [particleCount, glowColor]);

  const clear = useCallback(() => {
    timeouts.current.forEach(clearTimeout); timeouts.current = [];
    particles.current.forEach(p => gsap.to(p, { scale: 0, opacity: 0, duration: 0.3, ease: 'back.in(1.7)', onComplete: () => p.parentNode?.removeChild(p) }));
    particles.current = [];
  }, []);

  const spawn = useCallback(() => {
    if (!ref.current || !hovered.current) return;
    if (!inited.current) init();
    memo.current.forEach((p, i) => {
      const tid = setTimeout(() => {
        if (!hovered.current || !ref.current) return;
        const c = p.cloneNode(true); ref.current.appendChild(c); particles.current.push(c);
        gsap.fromTo(c, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(1.7)' });
        gsap.to(c, { x: (Math.random() - 0.5) * 100, y: (Math.random() - 0.5) * 100, rotation: Math.random() * 360, duration: 2 + Math.random() * 2, ease: 'none', repeat: -1, yoyo: true });
        gsap.to(c, { opacity: 0.3, duration: 1.5, ease: 'power2.inOut', repeat: -1, yoyo: true });
      }, i * 100);
      timeouts.current.push(tid);
    });
  }, [init]);

  useEffect(() => {
    if (disableAnimations || !ref.current) return;
    const el = ref.current;
    const enter = () => { hovered.current = true; spawn(); };
    const leave = () => { hovered.current = false; clear(); };
    const click = e => {
      if (!clickEffect) return;
      const r = el.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
      const d = Math.max(Math.hypot(x, y), Math.hypot(x - r.width, y), Math.hypot(x, y - r.height), Math.hypot(x - r.width, y - r.height));
      const rip = document.createElement('div');
      rip.style.cssText = `position:absolute;width:${d*2}px;height:${d*2}px;border-radius:50%;background:radial-gradient(circle,rgba(${glowColor},0.4) 0%,rgba(${glowColor},0.2) 30%,transparent 70%);left:${x-d}px;top:${y-d}px;pointer-events:none;z-index:1000;`;
      el.appendChild(rip);
      gsap.fromTo(rip, { scale: 0, opacity: 1 }, { scale: 1, opacity: 0, duration: 0.8, ease: 'power2.out', onComplete: () => rip.remove() });
    };
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('click', click);
    return () => { hovered.current = false; el.removeEventListener('mouseenter', enter); el.removeEventListener('mouseleave', leave); el.removeEventListener('click', click); clear(); };
  }, [spawn, clear, disableAnimations, clickEffect]);

  return <div ref={ref} className={`${className} mb-particle-container`} style={{ ...style, position: 'relative', overflow: 'hidden' }}>{children}</div>;
};

/* ─── GlobalSpotlight ───────────────────────────────────── */
const GlobalSpotlight = ({ gridRef, disableAnimations, enabled, spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS, glowColor = DEFAULT_GLOW_COLOR }) => {
  const spotRef = useRef(null);
  useEffect(() => {
    if (disableAnimations || !gridRef?.current || !enabled) return;
    const s = document.createElement('div');
    s.style.cssText = `position:fixed;width:800px;height:800px;border-radius:50%;pointer-events:none;background:radial-gradient(circle,rgba(${glowColor},0.15) 0%,rgba(${glowColor},0.08) 15%,rgba(${glowColor},0.04) 25%,transparent 70%);z-index:200;opacity:0;transform:translate(-50%,-50%);mix-blend-mode:screen;`;
    document.body.appendChild(s); spotRef.current = s;
    const onMove = e => {
      if (!spotRef.current || !gridRef.current) return;
      const sect = gridRef.current.closest('.mb-bento-section');
      const rect = sect?.getBoundingClientRect();
      const inside = rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      const cards = gridRef.current.querySelectorAll('.mb-card');
      if (!inside) { gsap.to(s, { opacity: 0, duration: 0.3 }); cards.forEach(c => c.style.setProperty('--glow-intensity', '0')); return; }
      const { proximity, fadeDistance } = calcSpot(spotlightRadius);
      let minD = Infinity;
      cards.forEach(c => {
        const cr = c.getBoundingClientRect();
        const d = Math.max(0, Math.hypot(e.clientX - (cr.left + cr.width / 2), e.clientY - (cr.top + cr.height / 2)) - Math.max(cr.width, cr.height) / 2);
        minD = Math.min(minD, d);
        setGlow(c, e.clientX, e.clientY, d <= proximity ? 1 : d <= fadeDistance ? (fadeDistance - d) / (fadeDistance - proximity) : 0, spotlightRadius);
      });
      gsap.to(s, { left: e.clientX, top: e.clientY, duration: 0.1 });
      gsap.to(s, { opacity: minD <= proximity ? 0.8 : minD <= fadeDistance ? ((fadeDistance - minD) / (fadeDistance - proximity)) * 0.8 : 0, duration: 0.2 });
    };
    const onLeave = () => { gridRef.current?.querySelectorAll('.mb-card').forEach(c => c.style.setProperty('--glow-intensity', '0')); gsap.to(s, { opacity: 0, duration: 0.3 }); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseleave', onLeave); s.parentNode?.removeChild(s); };
  }, [gridRef, disableAnimations, enabled, spotlightRadius, glowColor]);
  return null;
};

/* ─── MagicBento ────────────────────────────────────────── */
const MagicBento = ({
  textAutoHide = true, enableStars = true, enableSpotlight = true, enableBorderGlow = true,
  disableAnimations = false, spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  particleCount = DEFAULT_PARTICLE_COUNT, glowColor = DEFAULT_GLOW_COLOR,
  clickEffect = true, enableMagnetism = false,
}) => {
  const gridRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const chk = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    chk(); window.addEventListener('resize', chk); return () => window.removeEventListener('resize', chk);
  }, []);
  const off = disableAnimations || isMobile;
  const cls = ['mb-card', textAutoHide && 'mb-card--autohide', enableBorderGlow && 'mb-card--glow'].filter(Boolean).join(' ');

  return (
    <>
      {enableSpotlight && <GlobalSpotlight gridRef={gridRef} disableAnimations={off} enabled={enableSpotlight} spotlightRadius={spotlightRadius} glowColor={glowColor} />}
      <div className="mb-grid mb-bento-section" ref={gridRef}>
        {cardData.map(({ label, title, description, Visual }, i) => (
          <ParticleCard key={i} className={cls} style={{ '--glow-color': glowColor }} disableAnimations={off} particleCount={particleCount} glowColor={glowColor} clickEffect={clickEffect}>
            <div className="mb-card__header">
              <div className="mb-card__label">{label}</div>
            </div>
            <Visual />
            <div className="mb-card__footer">
              <h3 className="mb-card__title">{title}</h3>
              <p className="mb-card__desc">{description}</p>
            </div>
          </ParticleCard>
        ))}
      </div>
    </>
  );
};

export { ParticleCard, GlobalSpotlight };
export default MagicBento;
